import { useState, useEffect, useCallback } from 'react';
import { message } from 'antd';

export const useTickets = ({ caseId, apiClasses, provider, apiType }) => {
  const [ticketMessages, setTicketMessages] = useState([]);
  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchTicketMessages = useCallback(async () => {
    if (!caseId || !apiClasses?.TicketAPI) {
      return;
    }
    setLoading(true);
    setError(null);

    try {
      // Fetch both ticket and messages
      const [ticketResponse, messagesResponse] = await Promise.all([
        apiClasses.TicketAPI.getTicketByCaseId(caseId).catch(() => null),
        apiClasses.TicketAPI.getTicketChat({ caseId })
      ]);

      // Update ticket if available
      if (ticketResponse?.data?.success && ticketResponse.data.payload) {
        setTicket(ticketResponse.data.payload);
      }

      // Update messages
      const { data } = messagesResponse;
      if (!data.success || !data.payload) {
        throw new Error('Error fetching admin support chat');
      }

      const { payload } = data;
      const timestampMessageMapping = payload.data.map(message => ({ 
        ...message, 
        created: message.createAt 
      }));
      setTicketMessages(timestampMessageMapping.reverse());
    } catch (err) {
      setError(err.message || 'Failed to fetch admin messages');
      message.error('Error fetching admin support chat');
    } finally {
      setLoading(false);
    }
  }, [caseId, apiClasses?.TicketAPI]);

  const sendTicketChatMessage = useCallback(async (messageText, assignedAdminId = null) => {
    if (!caseId || !apiClasses?.TicketAPI) {
      return false;
    }

    const payload = {
      caseId: caseId,
      message: messageText,
    };

    if (assignedAdminId) {
      payload.assignedAdminId = assignedAdminId;
    }

    try {
      const { data } = await apiClasses.TicketAPI.sendChatMessage(payload);
      if (!data.success) {
        throw new Error('Failed to send ticket message');
      }
      const tempMessage = {
        _id: `temp-${Date.now()}`,
        message: messageText,
        createAt: new Date().toISOString(),
        created: new Date().toISOString(),
        ticketId: 'temp-ticket-id',
        readBy: [],
        chatOwnerDetails: {
          firstName: 'You',
          lastName: '',
          email: provider?.email || provider?.userEmail || '',
          _id: provider?._id || provider?.userId || 'temp-user-id'
        },
        isTemporary: true,
        isSending: true
      };
      
      setTicketMessages(list => list.concat(tempMessage));
      
      try {
        // Refetch both messages and ticket to get updated assignment
        const [ticketResponse, messagesResponse] = await Promise.all([
          apiClasses.TicketAPI.getTicketByCaseId(caseId).catch(() => null),
          apiClasses.TicketAPI.getTicketChat({ caseId })
        ]);

        // Update ticket if available
        if (ticketResponse?.data?.success && ticketResponse.data.payload) {
          setTicket(ticketResponse.data.payload);
        }

        // Update messages
        const { data } = messagesResponse;
        if (data.success && data.payload) {
          const { payload } = data;
          const timestampMessageMapping = payload.data.map(message => ({ 
            ...message, 
            created: message.createAt 
          }));
          
          const newMessages = timestampMessageMapping.reverse();
          const latestMessage = newMessages[0];
          
          if (latestMessage && latestMessage.message === messageText) {
            setTicketMessages(list => 
              list.map(msg => 
                msg._id === tempMessage._id 
                  ? { ...latestMessage, isSending: false }
                  : msg
              )
            );
          } else {
            setTicketMessages(newMessages);
          }
        }
      } catch (e) {
        setTicketMessages(list => list.filter(msg => msg._id !== tempMessage._id));
      }
      
      return true;
    } catch (err) {
      const errorMessage = 'Error sending ticket message';
      console.error(errorMessage, err)
      message.error(errorMessage);
      return false;
    }
  }, [caseId, apiClasses?.TicketAPI, provider]);

  const markTicketChatAsRead = useCallback(async () => {
    if (!ticket?._id || (!apiClasses?.PhysicianAPI && !apiClasses?.CaseTicketsAPI)) {
      return;
    }

    const api = apiType === 'physician' ? apiClasses?.PhysicianAPI : apiClasses?.CaseTicketsAPI;
    const payload = apiType === 'physician' ? { ticketId: ticket?._id } : ticket?._id;
    const userType = apiType === 'physician' ? 'physician' : 'admin';

    try {
      const { data } = await api.markTicketAsRead(payload);
      
      if (data.success) {
        setTicketMessages(state => {
          return state.map(message => {
            if (!message.readBy?.includes(userType)) {
              return { 
                ...message, 
                readBy: [...(message.readBy || []), userType] 
              };
            }
            return message;
          });
        });
      }
    } catch (err) {
      console.error('Error marking admin chat as read', err)
      message.error('Error marking admin chat as read')
    }
  }, [ticket, apiClasses?.PhysicianAPI, apiClasses?.CaseTicketsAPI, apiType]);

  const closeTicket = useCallback(async () => {
    if (!caseId || !apiClasses?.TicketAPI || !ticket?._id) {
      return;
    }

    try {
      await apiClasses.TicketAPI.updateTicket({ status: 'closed', ticketId: ticket._id, caseId });
      await fetchTicketMessages();
      return true;
    } catch (err) {
      console.error('Error closing ticket', err)
      message.error('Error closing ticket')
      return false;
    }

  }, [caseId, ticket, apiClasses?.TicketAPI, fetchTicketMessages]);

  useEffect(() => {
    fetchTicketMessages();
  }, [fetchTicketMessages]);

  return {
    ticketMessages,
    ticket,
    loading,
    error,
    sendTicketChatMessage,
    markTicketChatAsRead,
    fetchTicketMessages,
    closeTicket,
  };
};
